import React, { useMemo, useState } from 'react';
import CompanySearch from './CompanySearch';
import EmployeeSearch from './EmployeeSearch';
import EmployeeRetirementTable from './EmployeeRetirementTable';
import SeverancePanel from './retirement/SeverancePanel';
import LoadingSpinner from './common/LoadingSpinner';
import ErrorMessage from './common/ErrorMessage';
import { apiGet } from '../utils/api';
import { useDataFetch } from '../hooks/useApi';
import { useUrlState } from '../hooks/useUrlState';
import './RetirementSettlement.css';
import ExcelJS from 'exceljs';

function RetirementSettlement() {
  // 고른 기업·직원·유형·조회 개월은 주소에 둔다 — 주소를 보내면 같은 화면이 열린다.
  //   ?company=…&type=dc|db&employee=<직원 id 또는 contract_key>&months=n
  const [url, setUrl] = useUrlState();
  const [isNewCompany, setIsNewCompany] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const DEFAULT_MONTHS = 60;   // 조회 기간 기본값(최근 60개월)
  const months = Number(url.get('months')) || DEFAULT_MONTHS;
  const [retirementCart, setRetirementCart] = useState([]); // 퇴직정산 담기 목록
  const [showCart, setShowCart] = useState(false); // 슬라이드 창 표시 여부
  const selectedRetirementType = url.get('type'); // 'dc', 'db'
  const setSelectedRetirementType = (type) => setUrl({ type });
  const [sidebarFolded, setSidebarFolded] = useState(false);

  // 기업 목록 페칭
  const { 
    data: companies, 
    loading: companiesLoading, 
    error: companiesError, 
    refetch: refetchCompanies 
  } = useDataFetch(() => apiGet('/api/companies/'), []);
  const companyParam = url.get('company');
  const selectedCompany = useMemo(
    () => (companies || []).find((c) => String(c.id) === String(companyParam)) || null,
    [companies, companyParam],
  );
  // 기업을 고르면 직원 목록, 아니면 기업 검색을 보여 준다
  const viewMode = selectedCompany ? 'employee' : 'company';

  // 사용자 목록 페칭
  const { 
    data: users, 
    loading: usersLoading, 
    error: usersError, 
    refetch: refetchUsers 
  } = useDataFetch(() => apiGet('/api/companies/users/list'), []);

  // 직원 목록 페칭
  const { 
    data: employeesData, 
    error: employeesError, 
    refetch: refetchEmployees 
  } = useDataFetch(
    () => selectedCompany ? apiGet(`/api/employees/retirement/employees/${selectedCompany.id}?months=${months}`) : null,
    [selectedCompany, months]
  );

  const employeeParam = url.get('employee');
  const selectedEmployee = useMemo(
    () => ((employeesData && employeesData.employees) || []).find((e) => (e.employee_id || e.contract_key) === employeeParam) || null,
    [employeesData, employeeParam],
  );

  // 직원 데이터 페칭
  // 직원 식별: 대장에 직원 id 가 찍힌 달은 employee_id 로, 안 찍힌 달은 contract_key(성명·주민번호)로 찾으므로 둘 다 보낸다
  const employeeIdParam = (employee) => [
    employee.employee_id ? `employee_id=${encodeURIComponent(employee.employee_id)}` : '',
    employee.contract_key ? `contract_key=${encodeURIComponent(employee.contract_key)}` : '',
  ].filter(Boolean).join('&');

  const {
    data: employeeRetirementData,
    loading: employeeDataLoading,
    error: employeeDataError,
    refetch: refetchEmployeeData
  } = useDataFetch(
    () => selectedEmployee && selectedCompany ?
      apiGet(`/api/employees/retirement/employee-data/${selectedCompany.id}?${employeeIdParam(selectedEmployee)}`) :
      null,
    [selectedEmployee, selectedCompany]
  );

  const handleCompanySelect = (company) => {
    setUrl({ company: company ? company.id : null, employee: null });
    setIsNewCompany(false);
    setEmployeeData(null);
  };

  const handleAddCompany = () => {
    setIsNewCompany(true);
    setUrl({ company: null, employee: null });
    setEmployeeData(null);
  };

  const handleEditCompany = (company, isNew = false) => {
    if (isNew) {
      if (company) {
        // 신규 기업 생성 완료
        refetchCompanies();
        setUrl({ company: company.id, employee: null });
        setIsNewCompany(false);
      } else {
        // 신규 기업 등록 취소
        setIsNewCompany(false);
      }
    } else {
      // 기존 기업 수정 완료
      if (company) {
        refetchCompanies();
        setUrl({ company: company.id });
      }
    }
  };

  const handleEmployeeSelect = (employee) => {
    setUrl({ employee: employee ? (employee.employee_id || employee.contract_key) : null, planned_leave: null });
  };

  const handleBackToCompany = () => {
    setUrl({ company: null, employee: null });
    setEmployeeData(null);
  };

  const handleBackToEmployee = () => {
    setUrl({ employee: null });
    setEmployeeData(null);
  };

  const handleMonthsChange = (newMonths) => {
    setUrl({ months: newMonths === DEFAULT_MONTHS ? null : newMonths }, { replace: true });
  };

  // 퇴직정산 담기 기능
  const handleAddToCart = (employeeData, employee, company, yearlySummary = [], noLeaveWarning = false) => {
    console.log('=== 퇴직정산 담기 디버깅 ===');
    console.log('입력 데이터:', employeeData);
    console.log('임직원:', employee);
    console.log('기업:', company);
    
    // 중복 방지: 같은 임직원이 이미 담겨있는지 확인
    const existingEmployee = retirementCart.find(item => 
      item.employee.name === employee.name && 
      item.company.id === company.id
    );
    
    if (existingEmployee) {
      console.log('이미 담겨있는 임직원 발견:', existingEmployee);
      const confirmUpdate = window.confirm(
        `${employee.name}님의 퇴직정산이 이미 담겨있습니다.\n기존 데이터를 새 데이터로 업데이트하시겠습니까?`
      );
      
      if (!confirmUpdate) {
        console.log('사용자가 업데이트를 취소함');
        return;
      }
    }
    
    // employeeData는 선택된 셀들이 차감된 상태의 데이터
    // [{ '연월': 'yyyy년 m월', ..., '세전금액': 차감된값 }, ...] 형태의 배열
    const details = {};
    let totalAmount = 0;
    
    for (const item of employeeData) {
      const yearMonthKey = Object.keys(item)[0]; // 첫 번째 키 (연월)
      const yearMonthValue = item[yearMonthKey];
      const year = yearMonthValue.split('년')[0];
      const monthPart = yearMonthValue.split('년')[1];
      const month = monthPart ? monthPart.split('월')[0].trim() : '';
      
      // 마지막 컬럼의 값 (차감된 세전금액)
      const lastColumnKey = Object.keys(item)[Object.keys(item).length - 1];
      const netPriceRaw = item[lastColumnKey];
      
      // 숫자로 변환 (천 단위 구분자 제거)
      const netPrice = typeof netPriceRaw === 'string' 
        ? parseFloat(netPriceRaw.replace(/,/g, '')) || 0
        : parseFloat(netPriceRaw) || 0;
      
      console.log(`처리 중: ${yearMonthValue} -> ${year}년 ${month}월, 원본값: ${netPriceRaw}, 변환값: ${netPrice}`);
      
      // year -> month -> employee -> netprice 구조로 저장
      if (!details[year]) {
        details[year] = {};
      }
      if (!details[year][month]) {
        details[year][month] = {};
      }
      
      // 중복 방지: 같은 연월에 이미 값이 있으면 경고
      if (details[year][month][employee.name] !== undefined) {
        console.warn(`중복 데이터 발견: ${year}년 ${month}월 ${employee.name}님의 데이터가 이미 존재합니다.`);
        console.warn(`기존값: ${details[year][month][employee.name]}, 새값: ${netPrice}`);
      }
      
      details[year][month][employee.name] = netPrice;
      totalAmount += netPrice;
    }
    
    console.log('최종 details:', details);
    console.log('총 금액:', totalAmount);
    
    const cartItem = {
      id: `${company.id}_${employee.employee_id || employee.contract_key}`,
      employee: {
        name: employee.name,
        residentNumber: employee.id_number,
        joinDate: employee.hire_date
      },
      company: {
        id: company.id,
        name: company.name
      },
      totalAmount: totalAmount,
      details: details,
      // 연도별 DC 부담금 (산입개월수 기반)
      yearly: yearlySummary.map(yr => ({ ...yr })),
      retirementTotal: yearlySummary.reduce((s, yr) => s + (yr.contribution || 0), 0),
      noLeaveWarning: noLeaveWarning,
      // data: employeeData // 원본 데이터도 보관
    };

    console.log('카트 아이템 ID:', cartItem.id);
    console.log('카트 아이템:', cartItem);

    setRetirementCart(prev => {
      console.log('기존 카트:', prev);
      const existingIndex = prev.findIndex(item => item.id === cartItem.id);
      console.log('기존 아이템 인덱스:', existingIndex);
      
      if (existingIndex >= 0) {
        // 이미 담겨있으면 업데이트
        console.log('기존 아이템 업데이트');
        const updated = [...prev];
        updated[existingIndex] = cartItem;
        return updated;
      } else {
        // 새로 추가
        console.log('새 아이템 추가');
        return [...prev, cartItem];
      }
    });
    
    setShowCart(true);
  };

  // 담기에서 제거
  const handleRemoveFromCart = (itemId) => {
    setRetirementCart(prev => prev.filter(item => item.id !== itemId));
  };

  // 담기 전체 비우기
  const handleClearCart = () => {
    setRetirementCart([]);
    setShowCart(false);
  };

  // 엑셀 다운로드 함수 (ExcelJS 사용)
  const handleExcelDownload = async () => {
    if (retirementCart.length === 0) {
      alert('다운로드할 데이터가 없습니다.');
      return;
    }

    // 모든 카트 아이템의 데이터를 연도별로 통합
    const integratedData = {};
    const allEmployees = new Set();
    
    retirementCart.forEach(item => {
      Object.keys(item.details).forEach(year => {
        if (!integratedData[year]) {
          integratedData[year] = {};
        }
        Object.keys(item.details[year]).forEach(month => {
          if (!integratedData[year][month]) {
            integratedData[year][month] = {};
          }
          Object.keys(item.details[year][month]).forEach(employeeName => {
            const amount = item.details[year][month][employeeName];
            integratedData[year][month][employeeName] = amount;
            allEmployees.add(employeeName);
          });
        });
      });
    });
    
    const sortedEmployees = Array.from(allEmployees).sort();
    const sortedYears = Object.keys(integratedData).sort();
    
    // ExcelJS 워크북 생성
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('퇴직정산');
    
    // 컬럼 설정
    worksheet.columns = [
      { header: '연월', key: 'yearMonth', width: 12 },
      ...sortedEmployees.map(emp => ({ header: emp, key: emp, width: 18 }))
    ];
    
    // 헤더 스타일
    worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF4472C4' }
    };
    worksheet.getRow(1).alignment = { horizontal: 'center', vertical: 'middle' };
    
    // 헤더 테두리
    worksheet.getRow(1).eachCell((cell) => {
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
      };
    });
    
    let currentRow = 2;
    
    // 데이터 행들 추가
    sortedYears.forEach(year => {
      Array.from({length: 12}, (_, i) => i + 1).forEach(month => {
        const yearMonth = `${year}${month.toString().padStart(2, '0')}`;
        const monthData = integratedData[year][month];
        
        const rowData = { yearMonth };
        sortedEmployees.forEach(employee => {
          const amount = monthData && monthData[employee] ? monthData[employee] : 0;
          rowData[employee] = Math.round(amount);
        });
        
        const row = worksheet.addRow(rowData);
        
        // 연월 컬럼 스타일
        row.getCell('yearMonth').font = { bold: true };
        row.getCell('yearMonth').fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD9E2F3' }
        };
        row.getCell('yearMonth').alignment = { horizontal: 'center', vertical: 'middle' };
        
        // 데이터 컬럼들 스타일
        sortedEmployees.forEach(employee => {
          const cell = row.getCell(employee);
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.numFmt = '#,##0'; // 천 단위 구분자
        });
        
        // 모든 셀에 테두리 적용
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
          };
        });
        
        currentRow++;
      });
    });
    
    // 합계 행 추가
    const totalRowData = { yearMonth: '합계' };
    sortedEmployees.forEach(employee => {
      const totalAmount = sortedYears.reduce((yearSum, year) => {
        return yearSum + Array.from({length: 12}, (_, i) => i + 1).reduce((monthSum, month) => {
          const monthData = integratedData[year][month];
          const amount = monthData && monthData[employee] ? monthData[employee] : 0;
          return monthSum + amount;
        }, 0);
      }, 0);
      totalRowData[employee] = Math.round(totalAmount);
    });
    
    const totalRow = worksheet.addRow(totalRowData);
    totalRow.font = { bold: true };
    totalRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE3F2FD' }
    };
    totalRow.alignment = { horizontal: 'center', vertical: 'middle' };
    
    // 합계 행 테두리
    totalRow.eachCell((cell) => {
      cell.border = {
        top: { style: 'medium', color: { argb: 'FF2196F3' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
      };
    });
    
    // 합계 행 숫자 서식
    sortedEmployees.forEach(employee => {
      totalRow.getCell(employee).numFmt = '#,##0';
    });
    
    currentRow++;

    // 연도별 부담금 행 추가 (산입개월수 기반)
    const cartItemByName = (name) => retirementCart.find(it => it.employee.name === name);
    sortedYears.forEach(year => {
      const yearRowData = { yearMonth: `부담금(${year})` };
      sortedEmployees.forEach(employee => {
        const yr = cartItemByName(employee)?.yearly?.find(v => String(v.year) === String(year));
        const inherited = yr && yr.inheritedFrom !== null && yr.inheritedFrom !== undefined;
        yearRowData[employee] = yr
          ? `${Math.round(yr.contribution).toLocaleString()} (${yr.months.toFixed(2)}개월${inherited ? `, ${yr.inheritedFrom}년 승계` : ''})`
          : '-';
      });
      const yearRow = worksheet.addRow(yearRowData);
      yearRow.font = { bold: true };
      yearRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEDE7F6' } };
      yearRow.alignment = { horizontal: 'center', vertical: 'middle' };
      yearRow.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
      });
      currentRow++;
    });

    // 퇴직정산(총 부담금) 행 추가
    const retirementRowData = { yearMonth: '퇴직정산' };
    sortedEmployees.forEach(employee => {
      retirementRowData[employee] = Math.round(cartItemByName(employee)?.retirementTotal ?? 0);
    });

    const retirementRow = worksheet.addRow(retirementRowData);
    retirementRow.font = { bold: true };
    retirementRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF3E5F5' }
    };
    retirementRow.alignment = { horizontal: 'center', vertical: 'middle' };
    
    // 퇴직정산 행 테두리
    retirementRow.eachCell((cell) => {
      cell.border = {
        top: { style: 'medium', color: { argb: 'FF9C27B0' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
      };
    });
    
    // 퇴직정산 행 숫자 서식
    sortedEmployees.forEach(employee => {
      retirementRow.getCell(employee).numFmt = '#,##0';
    });
    
    currentRow++;
    
    // 총합계 행 추가 (직원별 총 부담금 합)
    const grandTotal = Math.round(retirementCart.reduce((s, it) => s + (it.retirementTotal || 0), 0));
    
    const grandTotalRow = worksheet.addRow({ yearMonth: '총합계' });
    grandTotalRow.font = { bold: true, size: 12 };
    grandTotalRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFFF3E0' }
    };
    grandTotalRow.alignment = { horizontal: 'center', vertical: 'middle' };
    
    // 총합계 셀 병합 및 값 설정
    worksheet.mergeCells(`B${currentRow}:${String.fromCharCode(65 + sortedEmployees.length)}${currentRow}`);
    const grandTotalCell = worksheet.getCell(`B${currentRow}`);
    grandTotalCell.value = grandTotal;
    grandTotalCell.numFmt = '#,##0';
    grandTotalCell.font = { bold: true, size: 12 };
    grandTotalCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFFF3E0' }
    };
    grandTotalCell.alignment = { horizontal: 'center', vertical: 'middle' };
    
    // 총합계 행 테두리
    grandTotalRow.eachCell((cell) => {
      cell.border = {
        top: { style: 'thick', color: { argb: 'FFFF9800' } },
        left: { style: 'thick', color: { argb: 'FFFF9800' } },
        bottom: { style: 'thick', color: { argb: 'FFFF9800' } },
        right: { style: 'thick', color: { argb: 'FFFF9800' } }
      };
    });
    
    // 파일명 생성 (현재 날짜 포함)
    const now = new Date();
    const dateStr = now.getFullYear() + 
                   String(now.getMonth() + 1).padStart(2, '0') + 
                   String(now.getDate()).padStart(2, '0');
    const fileName = `퇴직정산_${dateStr}.xlsx`;
    
    // 다운로드
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // 기업 선택으로 돌아갈 때 확인
  const handleBackToCompanyWithConfirm = () => {
    if (retirementCart.length > 0) {
      const confirmed = window.confirm(
        `담아둔 퇴직정산 정보가 있습니다.\n담은 항목: ${retirementCart.length}개\n계속하시겠습니까?`
      );
      if (!confirmed) {
        return;
      }
    }
    handleBackToCompany();
  };

  // 화면 전체를 '불러오는 중'으로 바꾸는 것은 처음 회사·사용자 목록을 받을 때만이다.
  // 직원을 누를 때마다 전체가 깜빡이지 않도록, 직원 목록·직원 자료의 로딩과 오류는 그 칸 안에서 보여 준다.
  const loading = companiesLoading || usersLoading;
  const error = companiesError || usersError;
  const sideError = employeesError || employeeDataError;

  if (loading) {
    return (
      <div className="retirement-settlement">
        <LoadingSpinner message="데이터를 불러오는 중..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="retirement-settlement">
        <div className="error-container">
          <ErrorMessage 
            message={error} 
            type="error" 
          />
          <button 
            className="retry-btn"
            onClick={() => {
              refetchCompanies();
              refetchUsers();
              if (selectedCompany) refetchEmployees();
              if (selectedEmployee) refetchEmployeeData();
            }}
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="retirement-settlement">
      <div className="retirement-layout">
        <aside className={`company-sidebar ${sidebarFolded ? 'folded' : ''}`}>
          <div className="sidebar-content">
            {viewMode === 'company' ? (
              <CompanySearch 
                companies={companies || []}
                onCompanySelect={handleCompanySelect}
                selectedCompany={selectedCompany}
                onAddCompany={handleAddCompany}
                onEditCompany={handleEditCompany}
                users={users || []}
              />
            ) : (
              <EmployeeSearch 
                employees={employeesData?.employees || []}
                onEmployeeSelect={handleEmployeeSelect}
                selectedEmployee={selectedEmployee}
                onBackToCompany={handleBackToCompanyWithConfirm}
                months={months}
                onMonthsChange={handleMonthsChange}
                company={selectedCompany}
              />
            )}
          </div>
          <button
            type="button"
            className="sidebar-fold-toggle"
            onClick={() => setSidebarFolded(!sidebarFolded)}
            title={sidebarFolded ? '기업 검색 펼치기' : '기업 검색 접기'}
            aria-label={sidebarFolded ? '기업 검색 펼치기' : '기업 검색 접기'}
          >
            <span className="sidebar-fold-icon">{sidebarFolded ? '›' : '‹'}</span>
          </button>
          {sidebarFolded && <span className="sidebar-fold-label">기업검색</span>}
        </aside>
        
        <main className="retirement-content">
          <div className="retirement-detail">
              {!selectedRetirementType ? (
                <div className="retirement-selection">
                  
                  <div className="retirement-message">
                    <div className="message-box">
                      <h2>퇴직정산 유형을 선택해주세요</h2>
                      <p>
                        처리하고자 하는 퇴직정산 유형을 선택해주세요.<br />
                        각 유형별로 다른 계산 방식과 절차가 적용됩니다.
                      </p>
                    </div>
                    
                    <div className="retirement-options">
                      <div className="option-cards">
                        <div 
                          className="option-card"
                          onClick={() => setSelectedRetirementType('dc')}
                        >
                          <div className="option-icon">💼</div>
                          <h3>퇴직연금 DC</h3>
                          <p>확정기여형 퇴직연금</p>
                          <div className="option-features">
                            <ul>
                              <li>개인별 계좌 관리</li>
                              <li>투자수익률 반영</li>
                              <li>세제 혜택</li>
                              <li>이직 시 이전 가능</li>
                            </ul>
                          </div>
                        </div>
                        
                        <div
                          className="option-card"
                          onClick={() => setSelectedRetirementType('db')}
                        >
                          <div className="option-icon">🏦</div>
                          <h3>일반퇴직금 (DB)</h3>
                          <p>확정급여형 퇴직금</p>
                          <div className="option-features">
                            <ul>
                              <li>퇴직자 정산: 평균임금·퇴직소득세·IRP 판정</li>
                              <li>퇴직금 시뮬레이션: 퇴사일별 예상 그래프</li>
                              <li>퇴직 전 3개월 임금 기준</li>
                              <li>휴직 기간 제외 · 통상임금 비교</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  
                  <div className="retirement-footer">
                    <p>문의사항이 있으시면 관리자에게 연락해주세요.</p>
                  </div>
                </div>
              ) : (
                <div className="retirement-panel">
                  <div className="panel-header">
                    <button
                      className="back-to-selection-btn"
                      onClick={() => setSelectedRetirementType(null)}
                    >
                      ← 유형 선택으로 돌아가기
                    </button>
                    <h2>
                      {selectedRetirementType === 'dc' ? '퇴직연금 DC' : '일반퇴직금 (DB)'}
                    </h2>
                  </div>

                  <div className="panel-content">
                    {selectedRetirementType === 'db' ? (
                      <div className="db-panel">
                        {selectedCompany ? (
                          <SeverancePanel key={selectedCompany.id} company={selectedCompany} />
                        ) : (
                          <div className="panel-message">
                            <h3>일반퇴직금</h3>
                            <p>왼쪽에서 기업을 고르면 퇴직자 정산과 퇴직금 시뮬레이션을 볼 수 있습니다.</p>
                          </div>
                        )}
                      </div>
                    ) : selectedRetirementType === 'dc' ? (
                      <div className="dc-panel">
                        {sideError && <div className="retirement-inline-error">{sideError}</div>}
                        {selectedEmployee && employeeDataLoading && !employeeRetirementData && (
                          <div className="retirement-inline-loading">불러오는 중…</div>
                        )}
                        {selectedEmployee && employeeRetirementData ? (
                          <div className={`retirement-data-display ${employeeDataLoading ? 'is-loading' : ''}`}>
                            <div className="retirement-data-header">
                              <h2>퇴직연금 DC - 퇴직정산 데이터</h2>
                              <div className="employee-info">
                                <span className="employee-name">{selectedEmployee?.name}</span>
                                <span className="employee-details">
                                  {selectedCompany?.name} | {selectedEmployee?.department} | {selectedEmployee?.position}
                                </span>
                              </div>
                            </div>
                            
                            <div className="retirement-data-content">
                              {employeeDataLoading && <div className="retirement-loading-veil">불러오는 중…</div>}
                              <EmployeeRetirementTable
                                employeeData={employeeRetirementData.data}
                                employee={selectedEmployee}
                                company={selectedCompany}
                                onAddToCart={handleAddToCart}
                                retirementMeta={employeeRetirementData}
                                plannedLeave={url.get('planned_leave')}
                                onPlannedLeaveChange={(value) => setUrl({ planned_leave: value }, { replace: true })}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="panel-message">
                            <h3>퇴직연금 DC 관리</h3>
                            <p>확정기여형 퇴직연금 관리를 위해 먼저 직원을 선택해주세요.</p>
                            <div className="selection-guide">
                              <p>왼쪽 사이드바에서 직원을 선택하면 해당 직원의 퇴직정산 데이터를 확인할 수 있습니다.</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
        </main>
        
        {/* 퇴직정산 담기 슬라이드 창 */}
        {showCart && (
          <div className="retirement-cart-slide">
            <div className="cart-header">
              <div className="cart-title-section">
              <h3>퇴직정산 담기 ({retirementCart.length})</h3>
                <button 
                  className="cart-close-btn"
                  onClick={() => setShowCart(false)}
                  title="접기"
                >
                  ✕ 접기
                </button>
              </div>
              <div className="cart-controls">
                <button 
                  className="cart-download-btn"
                  onClick={handleExcelDownload}
                  title="엑셀 다운로드"
                  disabled={retirementCart.length === 0}
                >
                  📊 엑셀 다운로드
                </button>
                <button 
                  className="cart-clear-btn"
                  onClick={handleClearCart}
                  title="전체 비우기"
                >
                  🗑️
                </button>
              </div>
            </div>
            
            <div className="cart-content">
              {retirementCart.length === 0 ? (
                <div className="cart-empty">
                  <p>담은 퇴직정산이 없습니다.</p>
                </div>
              ) : (
                <div className="cart-items">
                  {/* 카트 아이템 목록 - 간소화 */}
                  <div className="cart-item-list">
                  {retirementCart.map((item) => (
                      <div key={item.id} className="cart-item-summary">
                        <div className="cart-item-simple">
                          <span className="employee-name">{item.employee.name}</span>
                          <span className="employee-residentNumber">{item.employee.residentNumber}</span>
                          <span className="employee-joinDate">{item.employee.joinDate}</span>
                          <span className="employee-amount" title={`임금총액(차감후): ${item.totalAmount.toLocaleString()}원`}>
                            부담금 {(item.retirementTotal ?? 0).toLocaleString()}원
                          </span>
                          {item.noLeaveWarning && (
                            <span className="cart-leave-warning" title="퇴사일 미입력 — 마지막 임금대장 월까지 재직 간주">⚠</span>
                          )}
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveFromCart(item.id)}
                          title="제거"
                        >
                          ✕
                        </button>
                      </div>
                      {item.yearly?.length > 0 && (
                        <div className="cart-item-yearly">
                          {item.yearly.map(yr => (
                            <span key={yr.year} className="cart-yearly-chip">
                              {yr.year}: {yr.contribution.toLocaleString()} ({yr.months.toFixed(2)}개월{yr.inheritedFrom !== null && yr.inheritedFrom !== undefined ? `, ${yr.inheritedFrom}년 승계` : ''})
                            </span>
                          ))}
                        </div>
                      )}
                      </div>
                    ))}
                  </div>
                  
                  {/* 행렬 형태 통합 표시 */}
                  <div className="cart-table-container">
                    <div className="table-wrapper">
                      {(() => {
                      // 모든 카트 아이템의 데이터를 연도별로 통합
                      const integratedData = {};
                      const allEmployees = new Set();
                      const allYearMonths = new Set();
                      
                      retirementCart.forEach(item => {
                        Object.keys(item.details).forEach(year => {
                          if (!integratedData[year]) {
                            integratedData[year] = {};
                          }
                          Object.keys(item.details[year]).forEach(month => {
                            if (!integratedData[year][month]) {
                              integratedData[year][month] = {};
                            }
                            Object.keys(item.details[year][month]).forEach(employeeName => {
                              const amount = item.details[year][month][employeeName];
                              integratedData[year][month][employeeName] = amount;
                              allEmployees.add(employeeName);
                              allYearMonths.add(`${year}${month.toString().padStart(2, '0')}`);
                            });
                          });
                        });
                      });
                      
                      const sortedEmployees = Array.from(allEmployees).sort();
                      const sortedYears = Object.keys(integratedData).sort();
                      
                      return (
                        <div className="unified-table-section">
                          <div className="matrix-table-wrapper">
                            <table className="matrix-table">
                              <thead>
                                <tr>
                                  <th className="year-month-header">연월</th>
                                  {sortedEmployees.map(employee => {
                                    const employeeInfo = retirementCart.find(item => item.employee.name === employee);
                                    return (
                                      <th key={employee} className="employee-header">
                                        <div className="employee-info">
                                          <div className="employee-name">{employee}</div>
                                          <div className="employee-details">
                                            <div className="employee-id">{employeeInfo?.employee.residentNumber || '-'}</div>
                                            <div className="employee-date">
                                              입사일: {employeeInfo?.employee.joinDate ? new Date(employeeInfo.employee.joinDate).toLocaleDateString('ko-KR') : '-'}
                                            </div>
                        </div>
                        </div>
                                      </th>
                                    );
                                  })}
                                </tr>
                              </thead>
                              <tbody>
                                {sortedYears.map(year => 
                                  Array.from({length: 12}, (_, i) => i + 1).map((month) => {
                                    const yearMonth = `${year}${month.toString().padStart(2, '0')}`;
                                    const monthData = integratedData[year][month];
                                    
                                    return (
                                      <tr key={yearMonth}>
                                        <td className="year-month-cell">{yearMonth}</td>
                                        {sortedEmployees.map(employee => {
                                          const amount = monthData && monthData[employee] ? monthData[employee] : 0;
                                          return (
                                            <td key={employee} className="amount-cell">
                                              {amount > 0 ? Math.round(amount).toLocaleString() : '-'}
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    );
                                  })
                                )}
                                {/* 합계 행 */}
                                <tr className="total-row">
                                  <td className="total-label">합계</td>
                                  {sortedEmployees.map(employee => {
                                    const totalAmount = sortedYears.reduce((yearSum, year) => {
                                      return yearSum + Array.from({length: 12}, (_, i) => i + 1).reduce((monthSum, month) => {
                                        const monthData = integratedData[year][month];
                                        const amount = monthData && monthData[employee] ? monthData[employee] : 0;
                                        return monthSum + amount;
                                      }, 0);
                                    }, 0);
                                    return (
                                      <td key={employee} className="total-amount-cell">
                                        {totalAmount > 0 ? Math.round(totalAmount).toLocaleString() : '-'}
                                      </td>
                                    );
                                  })}
                                </tr>
                                {/* 연도별 부담금 행 (산입개월수 기반) */}
                                {sortedYears.map(year => (
                                  <tr key={`contrib-${year}`} className="year-contrib-row">
                                    <td className="retirement-label">부담금({year})</td>
                                    {sortedEmployees.map(employee => {
                                      const item = retirementCart.find(it => it.employee.name === employee);
                                      const yr = item?.yearly?.find(v => String(v.year) === String(year));
                                      if (!yr) return <td key={employee} className="retirement-amount-cell">-</td>;
                                      const inherited = yr.inheritedFrom !== null && yr.inheritedFrom !== undefined;
                                      return (
                                        <td
                                          key={employee}
                                          className="retirement-amount-cell"
                                          title={`산입 ${yr.months.toFixed(2)}개월${inherited ? ` — ${yr.inheritedFrom}년 부담금 승계` : ''}`}
                                        >
                                          {yr.contribution.toLocaleString()}{inherited ? ' (승계)' : ''}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                                {/* 퇴직정산(총 부담금) 행 */}
                                <tr className="retirement-row">
                                  <td className="retirement-label">퇴직정산</td>
                                  {sortedEmployees.map(employee => {
                                    const item = retirementCart.find(it => it.employee.name === employee);
                                    const total = Math.round(item?.retirementTotal ?? 0);
                                    return (
                                      <td key={employee} className="retirement-amount-cell">
                                        {total > 0 ? total.toLocaleString() : '-'}
                                      </td>
                                    );
                                  })}
                                </tr>
                                {/* 총합계 행 */}
                                <tr className="grand-total-row">
                                  <td className="grand-total-label">총합계</td>
                                  <td colSpan={sortedEmployees.length} className="grand-total-amount">
                                    {Math.round(retirementCart.reduce((s, it) => s + (it.retirementTotal || 0), 0)).toLocaleString()}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                        </div>
                        </div>
                      );
                    })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 접혀있을 때 펼치기 버튼 */}
        {!showCart && retirementCart.length > 0 && (
          <div className="cart-expand-button">
            <button 
              className="cart-expand-btn"
              onClick={() => setShowCart(true)}
              title="퇴직정산 담기 펼치기"
            >
              🛒 퇴직정산 담기 ({retirementCart.length})
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default RetirementSettlement;
